import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, customerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { ArrowLeft, User, Lock, Package, LogOut, Save, Eye, EyeOff, Check, Mail, Phone, CreditCard, Calendar, MapPin, Loader } from 'lucide-react';

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const { t, isGeorgian } = useTranslation();
  const [tab, setTab] = useState('info'); // info, password, history
  
  // Info form
  const [info, setInfo] = useState({ first_name: '', last_name: '', email: '', phone: '', id_number: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Password form
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  
  // History
  const [parcels, setParcels] = useState([]);
  const [loadingParcels, setLoadingParcels] = useState(false);

  useEffect(() => {
    // Load fresh profile
    authAPI.me().then(r => {
      setInfo({
        first_name: r.data.first_name || '',
        last_name: r.data.last_name || '',
        email: r.data.email || '',
        phone: r.data.phone || '',
        id_number: r.data.id_number || '',
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab]);

  const loadHistory = async () => {
    setLoadingParcels(true);
    try {
      const res = await customerAPI.myParcels();
      setParcels(res.data);
    } catch (e) { console.error(e); }
    setLoadingParcels(false);
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage(''); setError('');
    try {
      const res = await authAPI.updateProfile({
        first_name: info.first_name,
        last_name: info.last_name,
        phone: info.phone,
      });
      setUser(res.data);
      try { localStorage.setItem('cuser', JSON.stringify(res.data)); } catch (e) { /* ignore */ }
      setMessage(isGeorgian ? 'შენახულია' : 'Saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (isGeorgian ? 'შეცდომა' : 'Error'));
    }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage(''); setError('');
    if (newPwd !== confirmPwd) {
      setError(isGeorgian ? 'პაროლები არ ემთხვევა' : 'Passwords do not match');
      setSaving(false); return;
    }
    if (newPwd.length < 8) {
      setError(isGeorgian ? 'პაროლი უნდა იყოს მინ. 8 სიმბოლო' : 'Password must be at least 8 characters');
      setSaving(false); return;
    }
    try {
      await authAPI.changePassword(currentPwd, newPwd);
      setMessage(isGeorgian ? 'პაროლი შეიცვალა' : 'Password changed');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (isGeorgian ? 'შეცდომა' : 'Error'));
    }
    setSaving(false);
  };

  const statusLabel = (status) => {
    const map = {
      draft: isGeorgian ? 'მუშავდება' : 'Processing',
      stored: isGeorgian ? 'ბოქსში' : 'In locker',
      picked_up: isGeorgian ? 'გატანილია' : 'Picked up',
      expired: isGeorgian ? 'ვადა გაუვიდა' : 'Expired',
    };
    return map[status] || status;
  };

  const statusColor = (status) => {
    return status === 'picked_up' ? '#8BC63E' : status === 'stored' ? '#ffb020' : '#8789a0';
  };

  const sizeName = (size) => {
    return size === 'S' ? (isGeorgian ? 'პატარა' : 'Small')
      : size === 'M' ? (isGeorgian ? 'საშუალო' : 'Medium')
      : (isGeorgian ? 'დიდი' : 'Large');
  };

  const initial = (info.first_name?.[0] || info.email?.[0] || 'U').toUpperCase();

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.back} role="button" tabIndex={0} onClick={() => navigate('/home')}>
          <ArrowLeft size={18} /> {t('common.back')}
        </div>
        <span style={s.headerTitle}>{isGeorgian ? 'ჩემი პროფილი' : 'My Profile'}</span>
        <div style={{ width: 60 }} />
      </header>

      <div style={s.content}>
        {/* Profile header */}
        <div style={s.profileHeader}>
          <div style={s.avatar}>{initial}</div>
          <div style={s.profileName}>
            {(info.first_name + ' ' + info.last_name).trim() || info.email}
          </div>
          <div style={s.profileEmail}>{info.email}</div>
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          <div style={{...s.tab, ...(tab === 'info' ? s.tabActive : {})}} onClick={() => setTab('info')}>
            <User size={14}/> {isGeorgian ? 'ინფო' : 'Info'}
          </div>
          <div style={{...s.tab, ...(tab === 'password' ? s.tabActive : {})}} onClick={() => setTab('password')}>
            <Lock size={14}/> {isGeorgian ? 'პაროლი' : 'Password'}
          </div>
          <div style={{...s.tab, ...(tab === 'history' ? s.tabActive : {})}} onClick={() => setTab('history')}>
            <Package size={14}/> {isGeorgian ? 'ისტორია' : 'History'}
          </div>
        </div>

        {/* Messages */}
        {message && <div style={s.successMsg}><Check size={14}/> {message}</div>}
        {error && <div style={s.errorMsg}>{error}</div>}

        {/* Info tab */}
        {tab === 'info' && (
          <form onSubmit={saveInfo}>
            <label style={s.label}>{isGeorgian ? 'სახელი' : 'First Name'}</label>
            <input style={s.input} value={info.first_name}
              onChange={e => setInfo({...info, first_name: e.target.value})} />
            
            <label style={s.label}>{isGeorgian ? 'გვარი' : 'Last Name'}</label>
            <input style={s.input} value={info.last_name}
              onChange={e => setInfo({...info, last_name: e.target.value})} />
            
            <label style={s.label}>{isGeorgian ? 'ელ-ფოსტა' : 'Email'}</label>
            <input style={{...s.input, opacity: 0.6}} value={info.email} disabled />
            <div style={s.hint}>{isGeorgian ? 'ელ-ფოსტის შეცვლა არ შეიძლება' : 'Email cannot be changed'}</div>
            
            <label style={s.label}>{isGeorgian ? 'ტელეფონი' : 'Phone'}</label>
            <input style={s.input} value={info.phone} type="tel" inputMode="tel" autoComplete="tel-national"
              onChange={e => setInfo({...info, phone: e.target.value})} />
            
            <label style={s.label}>{isGeorgian ? 'პირადი ნომერი' : 'ID Number'}</label>
            <input style={{...s.input, opacity: 0.6}} value={info.id_number} disabled />
            <div style={s.hint}>{isGeorgian ? 'პირადი ნომრის შეცვლა არ შეიძლება' : 'ID number cannot be changed'}</div>
            
            <button type="submit" style={{...s.btn, opacity: saving ? 0.6 : 1}} disabled={saving}>
              <Save size={16}/> {saving ? (isGeorgian ? 'ინახება...' : 'Saving...') : (isGeorgian ? 'შენახვა' : 'Save Changes')}
            </button>
          </form>
        )}

        {/* Password tab */}
        {tab === 'password' && (
          <form onSubmit={changePassword}>
            <label style={s.label}>{isGeorgian ? 'მიმდინარე პაროლი' : 'Current Password'}</label>
            <div style={{position:'relative'}}>
              <input style={s.input} type={showPwd ? 'text' : 'password'} value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)} required />
              <div style={s.eyeBtn} onClick={() => setShowPwd(!showPwd)}>
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </div>
            </div>
            
            <label style={s.label}>{isGeorgian ? 'ახალი პაროლი' : 'New Password'}</label>
            <input style={s.input} type={showPwd ? 'text' : 'password'} value={newPwd}
              onChange={e => setNewPwd(e.target.value)} required minLength={8} />
            <div style={s.hint}>{isGeorgian ? 'მინ. 8 სიმბოლო, დიდი ასო, ციფრი და სიმბოლო' : 'Min 8 chars, uppercase, number and symbol'}</div>
            
            <label style={s.label}>{isGeorgian ? 'გაიმეორეთ პაროლი' : 'Confirm Password'}</label>
            <input style={s.input} type={showPwd ? 'text' : 'password'} value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)} required />
            
            <button type="submit" style={{...s.btn, opacity: saving ? 0.6 : 1}} disabled={saving}>
              <Lock size={16}/> {saving ? (isGeorgian ? 'ინახება...' : 'Saving...') : (isGeorgian ? 'პაროლის შეცვლა' : 'Change Password')}
            </button>
          </form>
        )}

        {/* History tab */}
        {tab === 'history' && (
          <div>
            {loadingParcels ? (
              <div style={s.loading}><Loader size={20} style={{animation:'spin 1s linear infinite'}}/> {isGeorgian ? 'იტვირთება...' : 'Loading...'}</div>
            ) : parcels.length === 0 ? (
              <div style={s.empty}>
                <Package size={40} color="#8a8da4" style={{marginBottom: 12}}/>
                <div>{isGeorgian ? 'ამანათები ჯერ არ გაქვთ' : 'No parcels yet'}</div>
              </div>
            ) : (
              parcels.map(p => (
                <div key={p.id} style={s.parcelCard}>
                  <div style={s.parcelHeader}>
                    <div style={s.parcelOrder}>{p.order_no}</div>
                    <div style={{...s.parcelStatus, color: statusColor(p.status), background: statusColor(p.status) + '20'}}>
                      {statusLabel(p.status)}
                    </div>
                  </div>
                  <div style={s.parcelRow}><MapPin size={12} color="#8789a0"/> {p.device_name} · {isGeorgian ? 'ბოქსი' : 'Box'} {p.box_name} · {sizeName(p.box_size)}</div>
                  <div style={s.parcelRow}><User size={12} color="#8789a0"/> {isGeorgian ? 'მიმღები' : 'To'}: {p.recipient_name}</div>
                  <div style={s.parcelRow}><Calendar size={12} color="#8789a0"/> {new Date(p.created_at).toLocaleString(isGeorgian ? 'ka-GE' : 'en')}</div>
                  {p.description && <div style={s.parcelDesc}>{p.description}</div>}
                </div>
              ))
            )}
          </div>
        )}

        {/* Logout */}
        <div style={{height: 1, background: '#252733', margin: '24px 0'}} />
        <button style={s.logoutBtn} onClick={() => { logout(); navigate('/'); }}>
          <LogOut size={16}/> {isGeorgian ? 'გასვლა' : 'Sign Out'}
        </button>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#07080c', color: '#e4e5ed' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #252733' },
  back: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8789a0', cursor: 'pointer' },
  headerTitle: { fontSize: 15, fontWeight: 600 },
  content: { maxWidth: 480, margin: '0 auto', padding: '24px 16px 40px' },
  profileHeader: { textAlign: 'center', marginBottom: 24 },
  avatar: { width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #8BC63E, #00b4d8)', color: '#07080c', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, marginBottom: 12 },
  profileName: { fontSize: 18, fontWeight: 700, color: '#e4e5ed', marginBottom: 4 },
  profileEmail: { fontSize: 13, color: '#8789a0' },
  tabs: { display: 'flex', gap: 4, background: '#0f1117', border: '1px solid #252733', borderRadius: 10, padding: 4, marginBottom: 20 },
  tab: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 12px', borderRadius: 7, fontSize: 13, color: '#8789a0', cursor: 'pointer', fontWeight: 500 },
  tabActive: { background: '#252733', color: '#8BC63E' },
  successMsg: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(139,198,62,0.1)', border: '1px solid rgba(139,198,62,0.3)', color: '#8BC63E', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
  errorMsg: { background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.3)', color: '#ff4466', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 14 },
  input: { width: '100%', background: '#161821', border: '1px solid #5c6078', borderRadius: 10, padding: '13px 14px', fontSize: 16, color: '#e4e5ed', outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: 11, color: '#8a8da4', marginTop: 4 },
  eyeBtn: { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#8789a0', cursor: 'pointer' },
  btn: { width: '100%', padding: '14px 0', background: '#8BC63E', color: '#07080c', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  logoutBtn: { width: '100%', padding: '12px 0', background: 'transparent', border: '1px solid rgba(255,68,102,0.3)', color: '#ff4466', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  loading: { textAlign: 'center', padding: 40, color: '#8789a0' },
  empty: { textAlign: 'center', padding: 40, color: '#8789a0' },
  parcelCard: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 16, marginBottom: 10 },
  parcelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  parcelOrder: { fontSize: 11, color: '#8a8da4', fontFamily: "'Space Mono', monospace" },
  parcelStatus: { fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase' },
  parcelRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#e4e5ed', marginBottom: 4 },
  parcelDesc: { marginTop: 8, padding: '8px 10px', background: '#1a1f36', borderRadius: 6, fontSize: 12, color: '#8789a0' },
};
